import React from 'react';
import { useBooking } from '../context/BookingContext';

const BookingSidebar = ({ onContinue }) => {
  const { state, formatCurrency, formatDisplayDate, calculateNights } = useBooking();

  const nights = calculateNights();
  const totalQtyInCart = state.cart.reduce((acc, item) => acc + item.quantity, 0);
  const requiredRooms = state.searchState?.room_guest?.length || 0;

  const isCheckoutEnabled = totalQtyInCart === requiredRooms && totalQtyInCart > 0;

  const [expandedSections, setExpandedSections] = React.useState({
    bookingDetails: true,
    selectedRooms: true,
    priceBreakdown: true,
    taxDetails: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Helper function to calculate per room pricing similar to RateOption
  const calculateRoomPricingForStay = (cartItem) => {
    const nights = calculateNights();
    let totalForStay = 0;
    let totalOriginalForStay = 0;
    let taxAmount = 0;
    
    // Check if this is a free night offer
    const isFreeNightOffer = cartItem.ratePlan?.offer?.discount_form === 'free_nights';
    const freeNights = cartItem.ratePlan?.offer?.no_free_nights || 0;
    const minStayNights = cartItem.ratePlan?.offer?.min_stay_nights || 1;
    
    if (isFreeNightOffer && nights >= minStayNights && freeNights > 0) {
      // Calculate free night pricing
      const payableNights = Math.max(nights - freeNights, 0);
      
      // For payable nights: use ORIGINAL price
      const priceForPayableNights = (cartItem.originalPrice || cartItem.roomPrice) * payableNights;
      
      // For free nights: use DISCOUNTED price
      const priceForFreeNights = cartItem.roomPrice * freeNights;
      
      totalForStay = priceForPayableNights + priceForFreeNights;
      totalOriginalForStay = (cartItem.originalPrice || cartItem.roomPrice) * nights;
      
      console.log(`Free night calculation for ${cartItem.roomName}:`);
      console.log(`  Nights: ${nights}, Free: ${freeNights}, Payable: ${payableNights}`);
      console.log(`  Original per night: ${cartItem.originalPrice || cartItem.roomPrice}`);
      console.log(`  Discounted per night: ${cartItem.roomPrice}`);
      console.log(`  Payable nights (${payableNights}) × Original: ${(cartItem.originalPrice || cartItem.roomPrice)} × ${payableNights} = ${priceForPayableNights}`);
      console.log(`  Free nights (${freeNights}) × Discounted: ${cartItem.roomPrice} × ${freeNights} = ${priceForFreeNights}`);
      console.log(`  Total for stay: ${totalForStay}`);
    } else {
      // Regular calculation
      totalForStay = cartItem.roomPrice * nights;
      totalOriginalForStay = (cartItem.originalPrice || cartItem.roomPrice) * nights;
    }
    
    // Calculate tax for the stay
    if (cartItem.taxData) {
      taxAmount = (cartItem.taxData.total_cgst_amount_after_tax || 0) +
                 (cartItem.taxData.total_sgst_amount_after_tax || 0) +
                 (cartItem.taxData.total_cess_amount || 0);
    } else {
      // Fallback tax calculation (18%)
      taxAmount = totalForStay * 0.18;
    }
    
    return {
      totalForStay,
      totalOriginalForStay,
      taxAmount,
      isFreeNightOffer,
      freeNights,
      payableNights: Math.max(nights - freeNights, 0),
      hasDiscount: cartItem.hasDiscount || (cartItem.originalPrice > cartItem.roomPrice),
      discountAmount: (cartItem.originalPrice - cartItem.roomPrice) * nights,
      roomPricePerNight: cartItem.roomPrice,
      originalPricePerNight: cartItem.originalPrice || cartItem.roomPrice
    };
  };

  // Main calculation function for display totals
  const calculateDisplayTotals = () => {
    const nights = calculateNights();
    
    if (state.cart.length === 0) {
      return {
        subtotal: 0,
        originalSubtotal: 0,
        taxes: 0,
        total: 0,
        finalTotal: 0,
        cgst: 0,
        sgst: 0,
        cess: 0,
        nights: nights,
        totalDiscount: 0,
        freeNightsSavings: 0
      };
    }
    
    let totalSubtotal = 0;
    let totalOriginalSubtotal = 0;
    let totalTaxes = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalCess = 0;
    let totalDiscount = 0;
    let freeNightsSavings = 0;
    
    // Calculate for each room in cart
    state.cart.forEach((item) => {
      const roomPricing = calculateRoomPricingForStay(item);
      
      totalSubtotal += roomPricing.totalForStay;
      totalOriginalSubtotal += roomPricing.totalOriginalForStay;
      totalTaxes += roomPricing.taxAmount;
      
      if (item.taxData) {
        totalCGST += item.taxData.total_cgst_amount_after_tax || 0;
        totalSGST += item.taxData.total_sgst_amount_after_tax || 0;
        totalCess += item.taxData.total_cess_amount || 0;
      }
      
      // Calculate discount
      if (roomPricing.hasDiscount) {
        totalDiscount += roomPricing.discountAmount;
      }
      
      // Calculate free nights savings
      if (roomPricing.isFreeNightOffer && roomPricing.freeNights > 0) {
        // Savings from free nights = original price for free nights
        const savingsFromFreeNights = roomPricing.originalPricePerNight * roomPricing.freeNights;
        freeNightsSavings += savingsFromFreeNights;
      }
    });
    
    const total = totalSubtotal + totalTaxes;
    
    return {
      subtotal: totalSubtotal,
      originalSubtotal: totalOriginalSubtotal,
      taxes: totalTaxes,
      total: total,
      finalTotal:Math.round(total),
      cgst: totalCGST,
      sgst: totalSGST,
      cess: totalCess,
      nights: nights,
      totalDiscount: totalDiscount,
      freeNightsSavings: freeNightsSavings,
      hasDiscount: totalDiscount > 0,
      hasFreeNights: freeNightsSavings > 0
    };
  };

  const displayTotals = calculateDisplayTotals();

  // Check for free nights offer
  const hasFreeNightsOffer = () => {
    return state.cart.some(item => 
      item.ratePlan?.offer?.discount_form === 'free_nights' && 
      item.ratePlan.offer.no_free_nights > 0
    );
  };

  // Get the maximum free nights count
  const getFreeNightsCount = () => {
    if (!hasFreeNightsOffer()) return 0;
    
    const freeNights = state.cart.map(item => 
      item.ratePlan?.offer?.discount_form === 'free_nights' 
        ? item.ratePlan.offer.no_free_nights 
        : 0
    );
    
    return Math.max(...freeNights);
  };

  // Get room display info with accurate pricing
const getRoomDisplayInfo = () => {
  const roomInfo = [];
  const nights = calculateNights();

  state.cart.forEach((item, index) => {
    const roomPricing = calculateRoomPricingForStay(item);
    
    // IMPORTANT: Get guest counts from multiple possible sources
    let adultsCount = 2; // Default
    let childrenCount = 0; // Default
    
    // Check all possible locations for guest counts (in priority order)
    if (item.guestInfo) {
      // From cart item's guestInfo object
      adultsCount = item.guestInfo.adults || item.adultsCount || 2;
      childrenCount = item.guestInfo.children || item.childrenCount || 0;
    } else if (item.originalGuestConfig) {
      // From original guest config
      adultsCount = item.originalGuestConfig.adults || 2;
      childrenCount = item.originalGuestConfig.children || 0;
    } else if (state.searchState?.room_guest) {
      // From searchState based on room number
      const roomGuest = state.searchState.room_guest[item.roomNumber - 1];
      if (roomGuest) {
        adultsCount = roomGuest.no_of_adults || 2;
        childrenCount = roomGuest.no_of_children || 0;
      } else {
        // Fallback to item properties
        adultsCount = item.adultsCount || 2;
        childrenCount = item.childrenCount || 0;
      }
    } else {
      // Last resort fallback
      adultsCount = item.adultsCount || 2;
      childrenCount = item.childrenCount || 0;
    }
    
    roomInfo.push({
      roomNumber: index + 1,
      roomName: item.roomName,
      rateName: item.rateName,
      roomPricePerNight: item.roomPrice,
      originalPricePerNight: item.originalPrice || item.roomPrice,
      totalForStay: roomPricing.totalForStay,
      taxForStay: roomPricing.taxAmount,
      isFreeNightOffer: roomPricing.isFreeNightOffer,
      freeNights: roomPricing.freeNights,
      payableNights: roomPricing.payableNights,
      hasDiscount: roomPricing.hasDiscount,
      discountAmount: roomPricing.discountAmount,
      adultsCount: adultsCount, // CORRECTED
      childrenCount: childrenCount, // CORRECTED
      taxData: item.taxData,
      ratePlan: item.ratePlan,
      guestInfo: item.guestInfo // Keep for debugging
    });
  });

  return roomInfo;
};

  const roomDisplayInfo = getRoomDisplayInfo();

  // Group rooms by type
  // Group rooms by type
const getGroupedRoomInfo = () => {
  const groups = {};
  const nights = calculateNights();

  if (state.cart.length === 0) {
    return [];
  }

  const roomGuestInfo = state.searchState.room_guest || [];

  state.cart.forEach((item, cartIndex) => {
    if (!groups[item.roomName]) {
      groups[item.roomName] = {
        name: item.roomName,
        count: 0,
        adults: 0,
        children: 0,
        totalBasePrice: 0,
        totalDiscountedPrice: 0
      };
    }

    groups[item.roomName].count += item.quantity;
    
    // CRITICAL FIX: Get guest counts from the correct source
    // Priority 1: From item.guestInfo
    // Priority 2: From item.originalGuestConfig
    // Priority 3: From searchState
    // Priority 4: From item.adultsCount/childrenCount
    
    let adultsToAdd = 0;
    let childrenToAdd = 0;
    
    if (item.guestInfo) {
      adultsToAdd = (item.guestInfo.adults || 0) * item.quantity;
      childrenToAdd = (item.guestInfo.children || 0) * item.quantity;
    } else if (item.originalGuestConfig) {
      adultsToAdd = (item.originalGuestConfig.adults || 0) * item.quantity;
      childrenToAdd = (item.originalGuestConfig.children || 0) * item.quantity;
    } else {
      // Try to get from searchState using room number
      const roomNumber = item.roomNumber;
      if (roomNumber && roomGuestInfo[roomNumber - 1]) {
        const guestInfo = roomGuestInfo[roomNumber - 1];
        adultsToAdd = (guestInfo.no_of_adults || 0) * item.quantity;
        childrenToAdd = (guestInfo.no_of_children || 0) * item.quantity;
      } else {
        // Fallback to item properties
        adultsToAdd = (item.adultsCount || 0) * item.quantity;
        childrenToAdd = (item.childrenCount || 0) * item.quantity;
      }
    }
    
    groups[item.roomName].adults += adultsToAdd;
    groups[item.roomName].children += childrenToAdd;

    // Calculate pricing for this item
    const roomPricing = calculateRoomPricingForStay(item);
    groups[item.roomName].totalBasePrice += roomPricing.totalOriginalForStay;
    groups[item.roomName].totalDiscountedPrice += roomPricing.totalForStay;
  });

  return Object.values(groups);
};

  const groupedRooms = getGroupedRoomInfo();

  // Calculate tax percentage for display
  const calculateTaxPercentage = () => {
    if (!displayTotals.subtotal || displayTotals.subtotal === 0) return '0%';
    const taxPercentage = (displayTotals.taxes / displayTotals.subtotal * 100).toFixed(1);
    return `${taxPercentage}%`;
  };

  const [isBreakdownOpen, setIsBreakdownOpen] = React.useState(false);

  return (
    <div className="lg:sticky top-24">
      <div className="bg-white shadow-lg lg:shadow-md rounded-xl lg:rounded-lg p-5 lg:p-4 border border-gray-100">
        <div className="space-y-4 lg:space-y-3">
          {/* Stay Details Section */}
          <div className="pb-4 lg:pb-3 border-b border-gray-200">
            <button
              onClick={() => toggleSection('bookingDetails')}
              className="flex items-center justify-between w-full text-left lg:cursor-default mb-2"
            >
              <h4 className="text-xs font-semibold text-text-primary uppercase tracking">Stay Details</h4>
              <svg
                className={`lg:hidden w-4 h-4 text-text-secondary transition-transform duration-200 ${expandedSections.bookingDetails ? 'rotate-180' : ''
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div
              className={`
                lg:block overflow-hidden transition-all duration-300 ease-in-out
                ${expandedSections.bookingDetails ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
              `}
            >
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase font-bold text-text-secondary tracking-tight">Check-in</span>
                    <span className="text-sm font-bold text-text-primary">
                      {formatDisplayDate(state.searchState.checkin)}
                    </span>
                    <span className="text-[11px] text-text-secondary">
                      {new Date(state.searchState.checkin).toLocaleDateString('en-US', { weekday: 'long' })} • 02:00 PM
                    </span>
                  </div>

                  <div className="self-center px-2 py-1 bg-gray-50 border border-gray-100 rounded text-[11px] font-bold text-brand-primary">
                    {nights} {nights === 1 ? 'Night' : 'Nights'}
                  </div>

                  <div className="flex flex-col text-right">
                    <span className="text-[10px] uppercase font-bold text-text-secondary tracking-tight">Check-out</span>
                    <span className="text-sm font-bold text-text-primary">
                      {formatDisplayDate(state.searchState.checkout)}
                    </span>
                    <span className="text-[11px] text-text-secondary">
                      {new Date(state.searchState.checkout).toLocaleDateString('en-US', { weekday: 'long' })} • 11:00 AM
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Rooms Section */}
          <div className="pb-3 border-b border-gray-100">
            <div className="flex justify-between items-end mb-2">
              <h4 className="text-xs font-bold text-text-primary uppercase tracking">
                Guest & Rooms
              </h4>
              {state.cart.length > 0 && (
                <div className="text-right leading-none">
                  <span className="text-sm font-semibold text-text-primary">
                    {totalQtyInCart} Rooms
                  </span>
                  <span className="mx-1 text-gray-300">•</span>
                  <span className="text-sm font-semibold text-text-primary">
                    {groupedRooms.reduce((acc, g) => acc + g.adults, 0)} Adults, {groupedRooms.reduce((acc, g) => acc + g.children, 0)} Children
                  </span>
                </div>
              )}
            </div>

            <div className={`lg:block overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.selectedRooms ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
              <div className="space-y-1">
                {groupedRooms.map((group, idx) => (
                  <div key={idx} className="flex justify-between items-center py-1 group">
                    <h5 className="font-medium text-sm text-text-primary truncate pr-4">
                      {group.count} × {group.name}
                    </h5>
                    <div className="text-[13px] font-medium text-text-secondary whitespace-nowrap flex-shrink-0">
                      {group.adults} Adults, {group.children} Children
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Price Summary Section */}
          <div className="border-gray-100">
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-text-primary uppercase tracking">
                Price
              </h4>

              {/* Free Nights Offer Badge */}
              {displayTotals.hasFreeNights && (
                <div className="mb-2 p-2 bg-green-50 border border-green-100 rounded-md">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-xs font-bold text-green-700">
                        FREE NIGHT OFFER APPLIED!
                      </p>
                      
                    </div>
                  </div>
                </div>
              )}

            

              {/* Tariff Row */}
              <div className="flex justify-between text-sm">
                <h5 className="text-text-primary font-medium">
                  Tariff for {nights} night{nights !== 1 ? 's' : ''}
                </h5>
                <span className="text-text-primary">
                  {formatCurrency(displayTotals.subtotal)}
                </span>
              </div>

            

              {/* Taxes Row */}
              <div className="flex justify-between text-sm">
                <h5 className="text-text-primary font-medium">Taxes & Fees</h5>
                <span className="text-text-primary">
                  {formatCurrency(displayTotals.taxes)}
                </span>
              </div>

              {/* Total Payable Row */}
              <div className="flex justify-between items-start pt-3 mt-1 border-t border-gray-100">
                <span className="text-sm font-bold text-text-primary uppercase tracking-tight mt-1">
                  Total Payable
                </span>
                <div className="text-right">
                  <span className="text-xl font-bold text-text-primary block leading-none">
                    {formatCurrency(displayTotals.finalTotal)}
                  </span>
                  
                  {state.cart.length > 0 && (
                    <button
                      onClick={() => setIsBreakdownOpen(true)}
                      className="text-[11px] font-bold text-brand-primary underline mt-1 hover:text-brand-primary-hover transition-colors"
                    >
                      View Price Breakup
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Checkout Section */}
          <div className="mt-6">
            {state.cart.length > 0 && totalQtyInCart !== requiredRooms && (
              <div className="mb-3 text-center">
                <p className="text-[12px] font-bold text-red-600 uppercase tracking-wide">
                  {totalQtyInCart < requiredRooms
                    ? `Please Add ${requiredRooms - totalQtyInCart} More Room${requiredRooms - totalQtyInCart > 1 ? 's' : ' to continue'}`
                    : `Limit Exceeded: Remove ${totalQtyInCart - requiredRooms} Room${totalQtyInCart - requiredRooms > 1 ? 's' : ''}`
                  }
                </p>
              </div>
            )}

            <button
              onClick={onContinue}
              disabled={!isCheckoutEnabled}
              className={`w-full bg-brand-accent text-text-dark-on-accent px-6 py-3.5 rounded-lg font-semibold text-base hover:bg-brand-accent-hover transition-all duration-200 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent shadow-md ${!isCheckoutEnabled ? 'opacity-50 cursor-not-allowed grayscale-[0.5]' : ''
                }`}
            >
              Continue to Checkout
            </button>
          </div>

          {/* Breakdown Modal */}
          {isBreakdownOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
              <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                  <h3 className="font-bold text-base text-text-primary">Price Breakup Detail</h3>
                  <button
                    onClick={() => setIsBreakdownOpen(false)}
                    className="p-1.5 hover:bg-gray-200 rounded-full transition-colors text-text-secondary"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="p-5 max-h-[60vh] overflow-y-auto">
                  <div className="space-y-4">
                    {/* Room-wise List */}
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold text-text-secondary uppercase tracking-[0.15em] mb-2">
                        Selected Accommodations
                      </h4>
                      {roomDisplayInfo.map((room, index) => (
                        <div key={index} className="py-3 border-b border-gray-100 last:border-0">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold bg-gray-100 text-text-secondary px-1.5 py-0.5 rounded">
                                  Room {room.roomNumber}
                                </span>
                                <h5 className="font-bold text-sm text-text-primary">{room.roomName}</h5>
                              </div>
                              <p className="text-[11px] text-brand-primary font-semibold mt-1 pl-8">
                                {room.rateName}
                              </p>
                              <p className="text-[11px] text-text-secondary pl-8">
                                {room.adultsCount} Adults, {room.childrenCount} Children
                              </p>
                              
                            
                            </div>
                            <div className="text-right">
                            
                              
                              <span className="font-bold text-sm text-text-primary">
                                {formatCurrency(room.totalForStay)}
                              </span>
                              
                              {room.taxForStay > 0 && (
                                <p className="text-[10px] text-text-secondary mt-0.5">
                                  Tax: {formatCurrency(room.taxForStay)}
                                </p>
                              )}
                              
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Financial Summary */}
                    <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
                      <div className="space-y-2">
                  
                        
                        {/* Taxes */}
                        <div className="flex justify-between text-sm text-text-secondary">
                          <span>Taxes & Fees:</span>
                          <span className="font-medium text-text-primary">
                            {formatCurrency(displayTotals.taxes)}
                          </span>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-3 mt-1 border-t-2 border-gray-100">
                        <span className="text-base font-bold text-text-primary">Grand Total</span>
                        <span className="text-xl font-bold text-text-primary">
                          {formatCurrency(displayTotals.finalTotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 pt-2">
                  <button
                    onClick={() => setIsBreakdownOpen(false)}
                    className="w-full py-3 bg-text-primary text-white rounded-xl font-bold shadow-md active:scale-[0.98] transition-all"
                  >
                    Close Detail View
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingSidebar;